/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  APPLICATION_LOAD_FAIL,
  APPLICATION_LOAD_SUCCESS,
  APPLICATION_SEARCH_SORTING_UPDATE,
  APPLICATIONS_FILTER_COUNTRY_VALUE_REMOVE,
  APPLICATIONS_FILTER_PROVINCE_VALUE_REMOVE,
  APPLICATIONS_FILTER_VALUE_ADD,
  APPLICATIONS_FILTER_VALUE_REMOVE,
  APPLICATIONS_FILTER_VALUE_UPDATE,
  APPLICATIONS_RESET,
  APPLICATIONS_SEARCH,
  APPLICATIONS_SEARCH_SUCCESS,
  ASSIGN_APPLICATION,
  ASSIGN_APPLICATION_FAIL,
  ASSIGN_APPLICATION_RESET,
  ASSIGN_APPLICATION_SUCCESS,
  BULK_ASSIGN_APPLICATION,
  BULK_ASSIGN_APPLICATION_FAIL,
  BULK_ASSIGN_APPLICATION_RESET,
  BULK_ASSIGN_APPLICATION_SUCCESS,
  DOWNLOAD_APPLICATIONS_TO_CSV,
  DOWNLOAD_APPLICATIONS_TO_CSV_CLEAN,
  DOWNLOAD_APPLICATIONS_TO_CSV_FAIL,
  DOWNLOAD_APPLICATIONS_TO_CSV_SUCCESS,
  FLAG_APPLICATION,
  FLAG_APPLICATION_FAIL,
  FLAG_APPLICATION_RESET,
  FLAG_APPLICATION_SUCCESS,
  GET_ASSIGN_APPLICATION,
  GET_ASSIGN_APPLICATION_FAIL,
  GET_ASSIGN_APPLICATION_SUCCESS,
  GET_AVAILABLE_ASSIGN_APPLICATION,
  GET_AVAILABLE_ASSIGN_APPLICATION_FAIL,
  GET_AVAILABLE_ASSIGN_APPLICATION_SUCCESS,
  SCORE_APPLICATION_FAIL,
  SCORE_APPLICATION_RESET,
  SCORE_APPLICATION_SUCCESS,
  UPDATE_APPLICATION_LISTING_CURRENT_PAGE,
  UPDATE_APPLICATION_LISTING_PAGE_SIZE,
  UPDATE_SELECTED_APPLICATION_EMAIL,
} from "Constants/ActionTypes";
import {
  defaultFilterValues,
  APPLICATION_COUNTRY_REGION,
  APPLICATION_STATE_PROVINCE,
} from "Constants/ApplicationConstants";
import { OperationStatus } from "Constants/OperationStatus";
import { defaultListingPageSize } from "Constants/ProfileConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";

import {
  ApplicationActionTypes,
  IApplicationsFilterValueAdd,
  IApplicationsFilterValueRemove,
  IApplicationsFilterValueUpdate,
  IApplicationSearchSortingChange,
  IAssignApplicationFail,
  IDownloadApplicationsToCSV,
  IDownloadApplicationsToCSVFail,
  IDownloadApplicationsToCSVSuccess,
  IFlagApplicationFail,
  IGetAssignApplicationFail,
  IGetAssignApplicationSuccess,
  ILoadApplicationFail,
  ILoadApplicationSuccess,
  IScoreApplicationFail,
  ISearchApplications,
  IUpdateCurrentPage,
  IUpdatePageSize,
  IUpdateSelectedApplicationEmail,
  IGetAvailableAssignApplicationSuccess,
  IGetAvailableAssignApplicationFail,
  IBulkAssignApplicationFail,
} from "./ApplicationAction";
import { IApplicationState } from "./IApplicationState";

const INITIAL_STATE: IApplicationState = {
  applications: [],
  applicationsFilterValues: defaultFilterValues,
  currentPage: 1,
  fileDownloadStatus: OperationStatus.Idle,
  fileDownloadErrorMessage: "",
  fileDownloadData: "",
  filteredCount: 0,
  isLoadingApplications: false,
  isLoadingSelectedApplication: false,
  loadApplicationsError: "",
  loadSelectedApplicationError: "",
  pageSize: defaultListingPageSize,
  searchText: "",
  selectedApplication: {
    id: 0,
    firstName: "",
    lastName: "",
    email: "",
    countryName: "",
    techArea: "",
    submittedOn: "",
    applicationStatus: "",
    modifiedDate: "",
    modifiedBy: "",
    isReadOnly: false,
    reviewStatus: "",
    technicalExpertise: [],
    industryFocus: [],
  },
  selectedApplicationEmail: "",
  tenant: TenantTypesEnum.MLSA,
  assignApplicationInfo: undefined,
  assignApplicationInfoStatus: OperationStatus.Idle,
  assignApplicationInfoErrorMessage: "",
  assignApplicationStatus: OperationStatus.Idle,
  assignApplicationErrorMessage: "",
  scoreApplicationStatus: OperationStatus.Idle,
  scoreApplicationErrorMessage: "",
  assignAvailableApplicationInfo: undefined,
  assignAvailableApplicationInfoStatus: OperationStatus.Idle,
  assignAvailableApplicationInfoErrorMessage: "",
  assignBulkApplicationStatus: OperationStatus.Idle,
  assignBulkApplicationErrorMessage: "",
  flagApplicationStatus: OperationStatus.Idle,
  flagApplicationErrorMessage: "",
  sortColumn: "",
  sortDescending: false,
};

const applyLoadAssignApplicationInfo = (state: IApplicationState): IApplicationState => ({
  ...state,
  assignApplicationInfoStatus: OperationStatus.Pending,
  assignApplicationInfoErrorMessage: "",
});

const applyLoadAssignApplicationInfoSuccess = (
  state: IApplicationState,
  action: IGetAssignApplicationSuccess
): IApplicationState => ({
  ...state,
  assignApplicationInfo: action.assignApplicationInfo,
  assignApplicationInfoStatus: OperationStatus.Succeeded,
  assignApplicationInfoErrorMessage: "",
});

const applyLoadAssignApplicationInfoFail = (
  state: IApplicationState,
  action: IGetAssignApplicationFail
): IApplicationState => ({
  ...state,
  assignApplicationInfoStatus: OperationStatus.Failed,
  assignApplicationInfoErrorMessage: action.errorMessage,
});

const applyAvailableAssignApplicationInfo = (state: IApplicationState): IApplicationState => ({
  ...state,
  assignAvailableApplicationInfoStatus: OperationStatus.Pending,
  assignAvailableApplicationInfoErrorMessage: "",
});

const applyAvailableAssignApplicationInfoSuccess = (
  state: IApplicationState,
  action: IGetAvailableAssignApplicationSuccess
): IApplicationState => ({
  ...state,
  assignAvailableApplicationInfo: action.assignAvailableApplicationInfo,
  assignAvailableApplicationInfoStatus: OperationStatus.Succeeded,
  assignAvailableApplicationInfoErrorMessage: "",
});

const applyAvailableAssignApplicationInfoFail = (
  state: IApplicationState,
  action: IGetAvailableAssignApplicationFail
): IApplicationState => ({
  ...state,
  assignAvailableApplicationInfoStatus: OperationStatus.Failed,
  assignAvailableApplicationInfoErrorMessage: action.errorMessage,
});

const applyAssignApplication = (state: IApplicationState): IApplicationState => ({
  ...state,
  assignApplicationStatus: OperationStatus.Pending,
  assignApplicationErrorMessage: "",
});

const applyAssignApplicationSuccess = (state: IApplicationState): IApplicationState => ({
  ...state,
  assignApplicationStatus: OperationStatus.Succeeded,
  assignApplicationErrorMessage: "",
});

const applyAssignApplicationFail = (
  state: IApplicationState,
  action: IAssignApplicationFail
): IApplicationState => ({
  ...state,
  assignApplicationStatus: OperationStatus.Failed,
  assignApplicationErrorMessage: action.errorMessage,
});

const applyAssignApplicationReset = (state: IApplicationState): IApplicationState => ({
  ...state,
  assignApplicationInfo: undefined,
  assignApplicationInfoStatus: OperationStatus.Idle,
  assignApplicationInfoErrorMessage: "",
  assignApplicationStatus: OperationStatus.Idle,
  assignApplicationErrorMessage: "",
});

const applyBulkAssignApplication = (state: IApplicationState): IApplicationState => ({
  ...state,
  assignBulkApplicationStatus: OperationStatus.Pending,
  assignBulkApplicationErrorMessage: "",
});

const applyBulkAssignApplicationSuccess = (state: IApplicationState): IApplicationState => ({
  ...state,
  assignBulkApplicationStatus: OperationStatus.Succeeded,
  assignBulkApplicationErrorMessage: "",
});

const applyBulkAssignApplicationFail = (
  state: IApplicationState,
  action: IBulkAssignApplicationFail
): IApplicationState => ({
  ...state,
  assignBulkApplicationStatus: OperationStatus.Failed,
  assignBulkApplicationErrorMessage: action.errorMessage,
});

const applyBulkAssignApplicationReset = (state: IApplicationState): IApplicationState => ({
  ...state,
  assignAvailableApplicationInfo: undefined,
  assignAvailableApplicationInfoStatus: OperationStatus.Idle,
  assignAvailableApplicationInfoErrorMessage: "",
  assignBulkApplicationStatus: OperationStatus.Idle,
  assignBulkApplicationErrorMessage: "",
});

const applyScoreApplicationSuccess = (state: IApplicationState): IApplicationState => ({
  ...state,
  scoreApplicationStatus: OperationStatus.Succeeded,
  scoreApplicationErrorMessage: "",
});

const applyScoreApplicationFail = (
  state: IApplicationState,
  action: IScoreApplicationFail
): IApplicationState => ({
  ...state,
  scoreApplicationStatus: OperationStatus.Failed,
  scoreApplicationErrorMessage: action.errorMessage,
});

const applyScoreApplicationReset = (state: IApplicationState): IApplicationState => ({
  ...state,
  scoreApplicationStatus: OperationStatus.Idle,
  scoreApplicationErrorMessage: "",
});

const applyFlagApplication = (state: IApplicationState): IApplicationState => ({
  ...state,
  flagApplicationStatus: OperationStatus.Pending,
});

const applyFlagApplicationSuccess = (state: IApplicationState): IApplicationState => ({
  ...state,
  flagApplicationStatus: OperationStatus.Succeeded,
  flagApplicationErrorMessage: "",
});

const applyFlagApplicationFail = (
  state: IApplicationState,
  action: IFlagApplicationFail
): IApplicationState => ({
  ...state,
  flagApplicationStatus: OperationStatus.Failed,
  flagApplicationErrorMessage: action.errorMessage,
});

const applyFlagApplicationReset = (state: IApplicationState): IApplicationState => ({
  ...state,
  flagApplicationStatus: OperationStatus.Idle,
  flagApplicationErrorMessage: "",
});

const applyLoadApplication = (
  state: IApplicationState,
  action: ILoadApplicationSuccess
): IApplicationState => ({
  ...state,
  selectedApplication: action.application,
  isLoadingSelectedApplication: false,
  loadSelectedApplicationError: "",
  // updateApplicationStatusError: "",
});

const applyLoadFailApplication = (
  state: IApplicationState,
  action: ILoadApplicationFail
): IApplicationState => ({
  ...state,
  loadSelectedApplicationError: action.loadSelectedApplicationError,
  isLoadingSelectedApplication: false,
});

const applySelectedApplication = (
  state: IApplicationState,
  action: IUpdateSelectedApplicationEmail
): IApplicationState => ({
  ...state,
  selectedApplicationEmail: action.selectedApplicationEmail,
  // updateApplicationStatusError: "",
  isLoadingSelectedApplication: true,
});

const applySearchApplications = (
  state: IApplicationState,
  action: ISearchApplications
): IApplicationState => ({
  ...state,
  applications: [],
  isLoadingApplications: true,
  searchText: action.searchText,
  currentPage: action.searchPage,
  loadApplicationsError: "",
  tenant: action.tenant,
  sortColumn: action.sortColumn,
  sortDescending: action.sortDescending,
  applicationsFilterValues: action.selectedApplicationsFilterValues, // added to retain the filter values
});

const applySearchApplicationsSuccess = (state: IApplicationState, action): IApplicationState => ({
  ...state,
  applications: action.applications,
  currentPage: action.searchPage,
  isLoadingApplications: false,
  filteredCount: action.filteredCount,
  loadApplicationsError: "",
});

const addApplicationsFilterValue = (
  state: IApplicationState,
  action: IApplicationsFilterValueAdd
): IApplicationState => ({
  ...state,
  applicationsFilterValues: state.applicationsFilterValues.concat([action.filterValue]),
});

const applyApplicationsReset = (): IApplicationState => INITIAL_STATE;

const removeApplicationsFilterValue = (
  state: IApplicationState,
  action: IApplicationsFilterValueRemove
): IApplicationState => ({
  ...state,
  applicationsFilterValues: state.applicationsFilterValues.filter(
    (item) =>
      !(item.fieldName === action.filterValue.fieldName && item.value === action.filterValue.value)
  ),
});

const removeApplicationsFilterProvinceValue = (state: IApplicationState): IApplicationState => ({
  ...state,
  applicationsFilterValues: state.applicationsFilterValues.filter(
    (item) => !(item.fieldName === APPLICATION_STATE_PROVINCE)
  ),
});

const removeApplicationsFilterCountryValue = (state: IApplicationState): IApplicationState => ({
  ...state,
  applicationsFilterValues: state.applicationsFilterValues.filter(
    (item) => !(item.fieldName === APPLICATION_COUNTRY_REGION)
  ),
});

const updateApplicationsFilterValue = (
  state: IApplicationState,
  action: IApplicationsFilterValueUpdate
): IApplicationState => ({
  ...state,
  applicationsFilterValues: state.applicationsFilterValues
    .filter((item) => !(item.fieldName === action.filterValue.fieldName))
    .concat([action.filterValue]),
});

const applyUpdateCurrentPage = (
  state: IApplicationState,
  action: IUpdateCurrentPage
): IApplicationState => ({
  ...state,
  currentPage: action.currentPage,
});

const applyUpdatePageSize = (
  state: IApplicationState,
  action: IUpdatePageSize
): IApplicationState => ({
  ...state,
  pageSize: action.pageSize,
});

const applyDownloadApplicationsToCSV = (
  state: IApplicationState,
  action: IDownloadApplicationsToCSV
): IApplicationState => ({
  ...state,
  fileDownloadStatus: OperationStatus.Pending,
  tenant: action.tenant,
});

const applyDownloadApplicationsToCSVSuccess = (
  state: IApplicationState,
  action: IDownloadApplicationsToCSVSuccess
): IApplicationState => ({
  ...state,
  fileDownloadStatus: OperationStatus.Succeeded,
  fileDownloadData: action.csvFile,
});

const applyDownloadApplicationsToCSVFailed = (
  state: IApplicationState,
  action: IDownloadApplicationsToCSVFail
): IApplicationState => ({
  ...state,
  fileDownloadStatus: OperationStatus.Failed,
  fileDownloadErrorMessage: action.errorMessage,
});

const applyDownloadApplicationsClean = (state: IApplicationState): IApplicationState => ({
  ...state,
  fileDownloadStatus: OperationStatus.Pending,
  fileDownloadData: "",
});

const applySearchSortingUpdate = (
  state: IApplicationState,
  action: IApplicationSearchSortingChange
): IApplicationState => ({
  ...state,
  sortColumn: action.sortColumn,
  sortDescending: action.sortDescending,
});

function applicationReducer(
  state = INITIAL_STATE,
  action: ApplicationActionTypes
): IApplicationState {
  switch (action.type) {
    case APPLICATION_LOAD_SUCCESS: {
      return applyLoadApplication(state, action);
    }
    case APPLICATION_LOAD_FAIL: {
      return applyLoadFailApplication(state, action);
    }
    case APPLICATIONS_SEARCH: {
      return applySearchApplications(state, action);
    }
    case APPLICATIONS_SEARCH_SUCCESS: {
      return applySearchApplicationsSuccess(state, action);
    }
    case APPLICATIONS_RESET: {
      return applyApplicationsReset();
    }
    case APPLICATIONS_FILTER_VALUE_ADD: {
      return addApplicationsFilterValue(state, action);
    }
    case APPLICATIONS_FILTER_VALUE_REMOVE: {
      return removeApplicationsFilterValue(state, action);
    }
    case APPLICATIONS_FILTER_COUNTRY_VALUE_REMOVE: {
      return removeApplicationsFilterCountryValue(state);
    }
    case APPLICATIONS_FILTER_PROVINCE_VALUE_REMOVE: {
      return removeApplicationsFilterProvinceValue(state);
    }
    case APPLICATIONS_FILTER_VALUE_UPDATE: {
      return updateApplicationsFilterValue(state, action);
    }
    case UPDATE_SELECTED_APPLICATION_EMAIL: {
      return applySelectedApplication(state, action);
    }
    case UPDATE_APPLICATION_LISTING_CURRENT_PAGE: {
      return applyUpdateCurrentPage(state, action);
    }
    case UPDATE_APPLICATION_LISTING_PAGE_SIZE: {
      return applyUpdatePageSize(state, action);
    }
    case DOWNLOAD_APPLICATIONS_TO_CSV: {
      return applyDownloadApplicationsToCSV(state, action);
    }
    case DOWNLOAD_APPLICATIONS_TO_CSV_SUCCESS: {
      return applyDownloadApplicationsToCSVSuccess(state, action);
    }
    case DOWNLOAD_APPLICATIONS_TO_CSV_FAIL: {
      return applyDownloadApplicationsToCSVFailed(state, action);
    }
    case DOWNLOAD_APPLICATIONS_TO_CSV_CLEAN: {
      return applyDownloadApplicationsClean(state);
    }
    case GET_ASSIGN_APPLICATION: {
      return applyLoadAssignApplicationInfo(state);
    }
    case GET_ASSIGN_APPLICATION_SUCCESS: {
      return applyLoadAssignApplicationInfoSuccess(state, action);
    }
    case GET_ASSIGN_APPLICATION_FAIL: {
      return applyLoadAssignApplicationInfoFail(state, action);
    }
    case GET_AVAILABLE_ASSIGN_APPLICATION: {
      return applyAvailableAssignApplicationInfo(state);
    }
    case GET_AVAILABLE_ASSIGN_APPLICATION_SUCCESS: {
      return applyAvailableAssignApplicationInfoSuccess(state, action);
    }
    case GET_AVAILABLE_ASSIGN_APPLICATION_FAIL: {
      return applyAvailableAssignApplicationInfoFail(state, action);
    }
    case ASSIGN_APPLICATION: {
      return applyAssignApplication(state);
    }
    case ASSIGN_APPLICATION_SUCCESS: {
      return applyAssignApplicationSuccess(state);
    }
    case ASSIGN_APPLICATION_FAIL: {
      return applyAssignApplicationFail(state, action);
    }
    case ASSIGN_APPLICATION_RESET: {
      return applyAssignApplicationReset(state);
    }
    case BULK_ASSIGN_APPLICATION: {
      return applyBulkAssignApplication(state);
    }
    case BULK_ASSIGN_APPLICATION_SUCCESS: {
      return applyBulkAssignApplicationSuccess(state);
    }
    case BULK_ASSIGN_APPLICATION_FAIL: {
      return applyBulkAssignApplicationFail(state, action);
    }
    case BULK_ASSIGN_APPLICATION_RESET: {
      return applyBulkAssignApplicationReset(state);
    }
    case SCORE_APPLICATION_SUCCESS: {
      return applyScoreApplicationSuccess(state);
    }
    case SCORE_APPLICATION_FAIL: {
      return applyScoreApplicationFail(state, action);
    }
    case SCORE_APPLICATION_RESET: {
      return applyScoreApplicationReset(state);
    }
    case FLAG_APPLICATION: {
      return applyFlagApplication(state);
    }
    case FLAG_APPLICATION_SUCCESS: {
      return applyFlagApplicationSuccess(state);
    }
    case FLAG_APPLICATION_FAIL: {
      return applyFlagApplicationFail(state, action);
    }
    case FLAG_APPLICATION_RESET: {
      return applyFlagApplicationReset(state);
    }
    case APPLICATION_SEARCH_SORTING_UPDATE: {
      return applySearchSortingUpdate(state, action);
    }
    default:
      return state;
  }
}

export default applicationReducer;
